/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { requireAuth } from '../lib/requireAuth';
import { authFetch } from '../lib/authFetch';
import { useWebsiteData, ImageData, WebsiteData } from '../hooks/useWebsiteData';
import { usePageMeta } from '../hooks/usePageMeta';
import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import FolderUploader from '../components/FolderUploader';
import { ImageModal } from '../components/ImageModal';
import { AdminNav } from '../components/AdminNav';

const LIVE_ENDPOINT = '/.netlify/functions/get-portfolio';

export const Route = createFileRoute('/projects/$folderId')({
  beforeLoad: () => requireAuth('/projects'),
  component: ProjectDetailPage,
});

function cloudinaryThumb(url: string, w: number, h: number): string {
  return url.replace('/upload/', `/upload/w_${w},h_${h},c_fill,q_auto/`);
}

function isHeroImage(img: ImageData): boolean {
  return img.context?.Hero === 'true' || img.context?.hero === 'true';
}

function ProjectDetailPage() {
  const { folderId } = Route.useParams();
  const navigate = useNavigate();
  const { data: websiteData, isLoading } = useWebsiteData(LIVE_ENDPOINT);
  const section = websiteData?.[folderId] ?? null;
  const queryClient = useQueryClient();
  usePageMeta(`${folderId} | Boyden Renovations`, undefined, true);

  const [images, setImages] = useState<ImageData[] | null>(null);
  const [pendingDeletes, setPendingDeletes] = useState<Set<string>>(new Set());
  const [heroOverrides, setHeroOverrides] = useState<Map<string, boolean>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSaved, setShowSaved] = useState(false);
  const [selectedImage, setSelectedImage] = useState<ImageData | null>(null);
  const [hoveredImage, setHoveredImage] = useState<{ img: ImageData; top: number; left: number } | null>(null);
  const [pendingReorder, setPendingReorder] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProject, setDeletingProject] = useState(false);
  const dragIndex = useRef<number | null>(null);

  const displayImages = images ?? section?._images ?? [];

  const hasChanges = pendingDeletes.size > 0 || heroOverrides.size > 0 || pendingReorder;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-xs tracking-[3px] uppercase text-neutral-dark/40">Loading...</p>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="px-6 py-10 font-sans text-neutral-dark">
        <p className="text-sm text-neutral-dark/50">Folder not found.</p>
        <Link to="/projects" className="text-xs tracking-[3px] uppercase text-neutral-dark/50 hover:text-neutral-dark transition-colors mt-4 inline-block">
          ← All Projects
        </Link>
      </div>
    );
  }

  if (images === null && section._images) {
    setImages(section._images);
  }

  const projectName =
    section._images[0]?.metadata?.website ??
    folderId.replace(/_/g, ' ');

  const toggleDelete = (publicId: string) => {
    setPendingDeletes((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) { next.delete(publicId); } else { next.add(publicId); }
      return next;
    });
  };

  const toggleHero = (img: ImageData) => {
    const currentHero = heroOverrides.has(img.public_id)
      ? heroOverrides.get(img.public_id)!
      : isHeroImage(img);
    setHeroOverrides((prev) => {
      const next = new Map(prev);
      const original = isHeroImage(img);
      const newValue = !currentHero;
      // If back to original, remove override
      if (newValue === original) {
        next.delete(img.public_id);
      } else {
        next.set(img.public_id, newValue);
      }
      return next;
    });
  };

  const handleDragStart = (index: number) => {
    dragIndex.current = index;
  };

  const handleDragEnter = (index: number) => {
    if (dragIndex.current === null || dragIndex.current === index) return;
    setImages((prev) => {
      const arr = [...(prev ?? [])];
      const [moved] = arr.splice(dragIndex.current!, 1);
      arr.splice(index, 0, moved);
      dragIndex.current = index;
      return arr;
    });
    setPendingReorder(true);
  };

  const handleDragEnd = () => {
    dragIndex.current = null;
  };

  const deleteProject = async () => {
    setDeletingProject(true);
    try {
      const res = await authFetch('/.netlify/functions/delete-folder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folderPath: `Website/${folderId}` }),
      });
      if (!res.ok) throw new Error(await res.text());
      queryClient.setQueryData<WebsiteData>(['websiteData', LIVE_ENDPOINT], (old) => {
        if (!old) return old;
        const next = { ...old };
        delete next[folderId];
        return next;
      });
      sessionStorage.setItem('deletedProject', section._metadata?.name?.replace(/_/g, ' ') ?? folderId);
      navigate({ to: '/projects' });
    } catch (err) {
      setDeletingProject(false);
      setShowDeleteModal(false);
      setSaveError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const discard = () => {
    setPendingDeletes(new Set());
    setHeroOverrides(new Map());
    setPendingReorder(false);
    setImages(section?._images ?? null);
    setSaveError(null);
  };

  const saveChanges = async () => {
    setSaving(true);
    setSaveError(null);

    const errors: string[] = [];

    // Process deletes
    for (const publicId of pendingDeletes) {
      try {
        const res = await authFetch('/.netlify/functions/delete-image', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ publicId }),
        });
        if (!res.ok) throw new Error(await res.text());
      } catch (err) {
        errors.push(`Delete failed for ${publicId.split('/').pop()}`);
      }
    }

    // Process hero overrides
    for (const [publicId, hero] of heroOverrides) {
      try {
        const img = displayImages.find((i: ImageData) => i.public_id === publicId);
        const existingContext = img?.context ?? {};
        const res = await authFetch('/.netlify/functions/update-context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ publicId, hero, existingContext }),
        });
        if (!res.ok) throw new Error(await res.text());
      } catch (err) {
        errors.push(`Context update failed for ${publicId.split('/').pop()}`);
      }
    }

    // Process order changes
    if (pendingReorder) {
      try {
        const updates = displayImages
          .filter((img: ImageData) => !pendingDeletes.has(img.public_id))
          .map((img: ImageData, index: number) => {
            const ctx = { ...(img.context ?? {}) };
            if (heroOverrides.has(img.public_id)) {
              const isHero = heroOverrides.get(img.public_id)!;
              if (isHero) { ctx['Hero'] = 'true'; }
              else { delete ctx['Hero']; delete ctx['hero']; }
            }
            return { public_id: img.public_id, order: index + 1, existingContext: ctx };
          });
        const res = await authFetch('/.netlify/functions/updateImageOrder', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates }),
        });
        if (!res.ok) throw new Error(await res.text());
        setPendingReorder(false);
      } catch (err) {
        errors.push('Order save failed');
      }
    }

    if (errors.length > 0) {
      setSaveError(errors.join(' · '));
    }

    // Apply changes to local state regardless of errors (best effort)
    setImages((prev) =>
      (prev ?? [])
        .filter((img) => !pendingDeletes.has(img.public_id))
        .map((img) => {
          if (!heroOverrides.has(img.public_id)) return img;
          const isHero = heroOverrides.get(img.public_id)!;
          const updatedContext = { ...img.context };
          if (isHero) {
            updatedContext['Hero'] = 'true';
          } else {
            delete updatedContext['Hero'];
            delete updatedContext['hero'];
          }
          return { ...img, context: updatedContext };
        })
    );

    setPendingDeletes(new Set());
    setHeroOverrides(new Map());
    setSaving(false);
    if (errors.length === 0) {
      setShowSaved(true);
      setTimeout(() => setShowSaved(false), 3000);
      // Update cache directly so navigation reflects saved changes immediately,
      // bypassing Cloudinary's search index delay
      queryClient.setQueryData<WebsiteData>(['websiteData', LIVE_ENDPOINT], (old) => {
        if (!old) return old;
        const applyHeroOverrides = (imgs: ImageData[]) =>
          imgs.map((img) => {
            if (!heroOverrides.has(img.public_id)) return img;
            const isHero = heroOverrides.get(img.public_id)!;
            const updatedContext = { ...img.context };
            if (isHero) { updatedContext['Hero'] = 'true'; }
            else { delete updatedContext['Hero']; delete updatedContext['hero']; }
            return { ...img, context: updatedContext };
          });

        const baseImages = pendingReorder
          ? (images ?? []).filter((img) => !pendingDeletes.has(img.public_id))
          : (old[folderId]?._images ?? []).filter((img) => !pendingDeletes.has(img.public_id));

        return { ...old, [folderId]: { ...old[folderId], _images: applyHeroOverrides(baseImages) } };
      });
    }
  };

  const siteButton =
    'text-sm tracking-[4px] font-sans font-light uppercase border px-4 py-2 hover:opacity-70 transition-opacity duration-300 disabled:opacity-30 disabled:cursor-not-allowed';

  return (
    <div className="font-sans text-neutral-dark">
      <AdminNav />
      <div className="max-w-3xl mx-auto px-6 py-10">

        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-serif tracking-widest uppercase mb-1">
              {folderId.replace(/_/g, ' ')}
            </h1>
            <p className="text-sm text-neutral-dark/50 tracking-wide">{projectName}</p>
            <p className="text-xs tracking-[3px] uppercase text-neutral-dark/30 mt-1">
              {displayImages.length} image{displayImages.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="text-xs tracking-[3px] uppercase text-neutral-dark/30 hover:text-red-500 transition-colors shrink-0 mt-1"
          >
            Delete Project
          </button>
        </div>

        {saveError && (
          <div className="mb-4 px-4 py-3 border border-red-300 bg-red-50/50">
            <p className="text-xs text-red-600">{saveError}</p>
          </div>
        )}

        {/* Image list */}
        <div className="divide-y divide-neutral-dark/10 border border-neutral-dark/20 mb-4">
          {displayImages.map((img: ImageData, index: number) => {
            const markedForDelete = pendingDeletes.has(img.public_id);
            const heroValue = heroOverrides.has(img.public_id)
              ? heroOverrides.get(img.public_id)!
              : isHeroImage(img);
            const heroChanged = heroOverrides.has(img.public_id);
            const filename = img.context?.filename ?? img.original_filename ?? img.public_id.split('/').pop() ?? img.public_id;

            return (
              <div
                key={img.public_id}
                draggable={!markedForDelete}
                onDragStart={() => handleDragStart(index)}
                onDragEnter={() => handleDragEnter(index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => e.preventDefault()}
                className={`flex items-center gap-4 px-4 py-3 transition-all duration-200 ${markedForDelete ? 'opacity-40' : 'cursor-grab active:cursor-grabbing'}`}
              >
                <svg className="w-4 h-4 text-neutral-dark/20 shrink-0 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M7 2a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM7 8a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-6 6a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
                </svg>
                <img
                  src={cloudinaryThumb(img.secure_url, 112, 112)}
                  alt={filename}
                  className={`w-14 h-14 object-cover shrink-0 cursor-zoom-in ${markedForDelete ? 'grayscale' : ''}`}
                  loading="lazy"
                  onClick={() => !markedForDelete && setSelectedImage(img)}
                  onMouseEnter={(e) => {
                    if (markedForDelete) return;
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoveredImage({ img, top: rect.top + rect.height / 2, left: rect.right + 12 });
                  }}
                  onMouseLeave={() => setHoveredImage(null)}
                />

                <div className="flex-1 min-w-0">
                  <p className={`text-sm text-neutral-dark/80 truncate ${markedForDelete ? 'line-through' : ''}`}>
                    {filename}
                  </p>
                  <p className="text-xs text-neutral-dark/40">
                    {img.width} × {img.height}
                    {markedForDelete && <span className="ml-2 text-red-400">· marked for deletion</span>}
                  </p>
                </div>

                <label className={`flex items-center gap-2 shrink-0 select-none ${markedForDelete ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    checked={heroValue}
                    disabled={markedForDelete || saving}
                    onChange={() => toggleHero(img)}
                    className="w-4 h-4 accent-neutral-dark"
                  />
                  <span className={`text-xs tracking-[2px] uppercase ${heroChanged ? 'text-neutral-dark' : 'text-neutral-dark/40'}`}>
                    Hero
                  </span>
                </label>

                <button
                  onClick={() => toggleDelete(img.public_id)}
                  disabled={saving}
                  className={`shrink-0 transition-colors disabled:opacity-30 px-1 ${markedForDelete ? 'text-red-400 hover:text-neutral-dark/40' : 'text-neutral-dark/30 hover:text-red-500'}`}
                  aria-label={markedForDelete ? 'Undo delete' : 'Mark for deletion'}
                >
                  {markedForDelete ? (
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              </div>
            );
          })}
        </div>

        {/* Saved confirmation */}
        {showSaved && !hasChanges && (
          <div className="mb-12 px-4 py-3 border border-neutral-dark/20 bg-neutral-dark/5 flex items-center gap-2">
            <p className="text-xs tracking-[2px] uppercase text-neutral-dark/60">Changes saved</p>
          </div>
        )}

        {/* Pending changes bar */}
        {hasChanges && (
          <div className="mb-12 px-4 py-3 border border-neutral-dark/20 bg-neutral-dark/5 flex items-center justify-between gap-4">
            <p className="text-xs tracking-[2px] uppercase text-neutral-dark/60">
              {[
                pendingDeletes.size > 0 && `${pendingDeletes.size} deletion${pendingDeletes.size !== 1 ? 's' : ''}`,
                heroOverrides.size > 0 && `${heroOverrides.size} hero change${heroOverrides.size !== 1 ? 's' : ''}`,
              ].filter(Boolean).join(' · ')} pending
            </p>
            <div className="flex items-center gap-3">
              <button onClick={discard} disabled={saving} className={`${siteButton} border-neutral-dark/30 text-neutral-dark/50`}>
                Discard
              </button>
              <button onClick={saveChanges} disabled={saving} className={`${siteButton} border-neutral-dark text-neutral-dark`}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        )}

        {/* Add more images */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xs tracking-[3px] uppercase text-neutral-dark/50">Add Images</h2>
            <p className="text-xs text-neutral-dark/30 tracking-wide">→ {folderId}</p>
          </div>
          <FolderUploader folder={folderId} projectName={projectName} startOrder={displayImages.length + 1} />
        </div>

        <div className="mt-10 pt-6 border-t border-neutral-dark/10 flex justify-start">
          <Link to="/projects" className={`${siteButton} border-neutral-dark/40 text-neutral-dark/60`}>
            ← All Projects
          </Link>
        </div>
      </div>

      {hoveredImage && (
        <div
          className="fixed z-50 pointer-events-none shadow-2xl border border-neutral-dark/10"
          style={{ top: hoveredImage.top, left: hoveredImage.left, transform: 'translateY(-50%)' }}
        >
          <img
            src={cloudinaryThumb(hoveredImage.img.secure_url, 600, 400)}
            alt={hoveredImage.img.public_id}
            className="w-64 h-auto"
          />
        </div>
      )}

      {selectedImage && (
        <ImageModal
          imageUrl={selectedImage.secure_url}
          alt={selectedImage.public_id.split('/').pop() ?? selectedImage.public_id}
          onClose={() => setSelectedImage(null)}
        />
      )}

      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-dark/40">
          <div className="bg-white border border-neutral-dark/20 px-8 py-8 max-w-sm w-full mx-4 flex flex-col gap-6">
            <div>
              <p className="text-sm tracking-[3px] uppercase font-sans mb-2">Delete Project</p>
              <p className="text-sm text-neutral-dark/60 font-sans">
                Are you sure you want to delete <span className="text-neutral-dark">{projectName}</span>? This will permanently remove all images and cannot be undone.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={deleteProject}
                disabled={deletingProject}
                className={`${siteButton} border-red-400 text-red-500 hover:opacity-70`}
              >
                {deletingProject ? 'Deleting...' : 'Delete'}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deletingProject}
                className={`${siteButton} border-neutral-dark/30 text-neutral-dark/50`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
